import { logger } from '~/utils/logger';
import { useNotificationStore } from "~/stores/notifications";
import { useWebSocket } from "~/composables/utils/useWebSocket";

export function useNotifications() {
  const store = useNotificationStore();
  const websocket = useWebSocket();

  /**
   * Initialize notification system
   */
  const initialize = async () => {
    logger.log("[useNotifications] Initializing notification system...");

    // Initialize store
    await store.initialize();

    // Request browser notification permission early
    await requestNotificationPermission();

    // Connect WebSocket
    websocket.connect();

    // Listen for admin notifications (for ADMIN and BOOKING_MANAGER roles)
    const handleAdminNotification = (payload: any) => {
      logger.log("[useNotifications] Received admin notification:", payload);
      store.addAdminNotification(payload);
    };

    // Listen for user notifications (for all users including regular customers)
    const handleUserNotification = (payload: any) => {
      logger.log("[useNotifications] Received user notification:", payload);
      store.addAdminNotification(payload); // Same handler, works for all notification types
    };

    // Remove any existing listeners to prevent duplicates
    websocket.off("admin:notification");
    websocket.off("notification:new");

    // Register listeners
    websocket.onAdminNotification(handleAdminNotification);
    websocket.onNotification(handleUserNotification);

    logger.log("[useNotifications] Notification system initialized");
  };

  /**
   * Request browser notification permission
   */
  const requestNotificationPermission = async () => {
    if (!("Notification" in window)) {
      logger.warn("[Notification] Browser notifications not supported");
      return false;
    }

    if (Notification.permission === "granted") {
      logger.log("[Notification] Permission already granted");
      return true;
    }

    if (Notification.permission !== "denied") {
      logger.log("[Notification] Requesting permission...");
      const permission = await Notification.requestPermission();
      logger.log("[Notification] Permission result:", permission);
      return permission === "granted";
    }

    logger.warn("[Notification] Permission denied");
    return false;
  };

  /**
   * Subscribe to push notifications
   */
  const subscribeToPush = async () => {
    try {
      const config = useRuntimeConfig();
      const baseURL = config.public.apiBase as string;

      const registration = await navigator.serviceWorker.ready;

      // Get VAPID public key from server
      logger.log(
        "[Push] Fetching VAPID key from:",
        `${baseURL}/notifications/vapid-key`
      );
      const response = await $fetch<{ publicKey: string }>(
        "/notifications/vapid-key",
        {
          baseURL,
          credentials: "include",
        }
      );

      logger.log("[Push] VAPID response:", response);

      if (!response || !response.publicKey) {
        throw new Error("VAPID public key not received from server");
      }

      const { publicKey } = response;
      logger.log(
        "[Push] VAPID key received:",
        publicKey.substring(0, 20) + "..."
      );

      // Subscribe to push
      const subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(publicKey) as BufferSource,
      });

      logger.log("[Push] Subscription created, sending to server...");

      // Convert subscription to backend format
      const subscriptionJSON = subscription.toJSON();
      const p256dhKey = subscriptionJSON.keys?.p256dh;
      const authKey = subscriptionJSON.keys?.auth;

      if (!p256dhKey || !authKey) {
        throw new Error("Subscription keys are missing");
      }

      // Send subscription to server in the format backend expects
      await $fetch("/notifications/push/subscribe", {
        method: "POST",
        baseURL,
        credentials: "include",
        body: {
          endpoint: subscription.endpoint,
          p256dh: p256dhKey,
          auth: authKey,
          userAgent: navigator.userAgent,
        },
      });

      logger.log("[Push] ✓ Successfully subscribed to push notifications");
      return true;
    } catch (error) {
      logger.error("Error subscribing to push:", error);
      return false;
    }
  };

  /**
   * Unsubscribe from push notifications
   */
  const unsubscribeFromPush = async () => {
    try {
      const config = useRuntimeConfig();
      const baseURL = config.public.apiBase as string;

      const registration = await navigator.serviceWorker.ready;
      const subscription = await registration.pushManager.getSubscription();

      if (subscription) {
        await subscription.unsubscribe();
        await $fetch("/notifications/push/unsubscribe", {
          method: "POST",
          baseURL,
          credentials: "include",
          body: { endpoint: subscription.endpoint },
        });
      }

      return true;
    } catch (error) {
      logger.error("Error unsubscribing from push:", error);
      return false;
    }
  };

  /**
   * Check if push is subscribed
   */
  const isPushSubscribed = async () => {
    if (!("serviceWorker" in navigator) || !("PushManager" in window)) {
      return false;
    }

    try {
      // Add a timeout to prevent hanging forever
      const registrationPromise = navigator.serviceWorker.ready;
      const timeoutPromise = new Promise<null>((_, reject) =>
        setTimeout(() => reject(new Error("Service worker timeout")), 1000)
      );

      const registration = await Promise.race([
        registrationPromise,
        timeoutPromise,
      ]);

      if (!registration) {
        return false;
      }

      const subscription = await registration.pushManager.getSubscription();
      return subscription !== null;
    } catch (error) {
      logger.warn("[isPushSubscribed] Error:", error);
      return false;
    }
  };

  /**
   * Play notification sound
   */
  const playNotificationSound = () => {
    try {
      const audio = new Audio("/sounds/notification.mp3");
      audio.volume = 0.5;
      audio.play().catch((err) => {
        logger.warn("Could not play notification sound:", err);
      });
    } catch (error) {
      logger.error("Error playing notification sound:", error);
    }
  };

  return {
    // State
    notifications: computed(() => store.sortedNotifications),
    unreadNotifications: computed(() => store.unreadNotifications),
    urgentNotifications: computed(() => store.urgentNotifications),
    unreadCount: computed(() => store.unreadCount),
    hasUnread: computed(() => store.hasUnread),
    loading: computed(() => store.loading),

    // Actions
    initialize,
    markAsRead: store.markAsRead,
    markAllAsRead: store.markAllAsRead,
    archiveNotification: store.archiveNotification,
    fetchNotifications: store.fetchNotifications,

    // Browser notifications
    requestNotificationPermission,
    subscribeToPush,
    unsubscribeFromPush,
    isPushSubscribed,
    playNotificationSound,
  };
}

// Helper functions
function urlBase64ToUint8Array(base64String: string): Uint8Array {
  if (!base64String) {
    throw new Error("VAPID key is undefined or empty");
  }

  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);

  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }

  return outputArray;
}

function arrayBufferToBase64(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer);
  let binary = "";
  for (let i = 0; i < bytes.byteLength; i++) {
    binary += String.fromCharCode(bytes[i] || 0);
  }
  return window.btoa(binary);
}
